import React from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { HybridAvatar } from '../components/ui/Avatar';
import { AssessmentHistoryCard } from '../components/dashboard/AssessmentHistoryCard';
import { useAssessmentStatus } from '../hooks/useAssessment';
import type { AssessmentAttempt, DetailedAssessmentResult } from '../types/assessment.types';

export const DashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const { data: assessmentStatus, isLoading: isLoadingStatus } = useAssessmentStatus();

  // Verificação para evitar erros de TypeScript quando assessmentStatus é undefined
  const statusData = assessmentStatus as {
    hasCompleted?: boolean;
    hasActive?: boolean;
    activeAssessment?: AssessmentAttempt;
    completedAssessment?: DetailedAssessmentResult;
  } | undefined;
  
  const hasCompleted = Boolean(statusData?.hasCompleted);
  const hasActive = Boolean(statusData?.hasActive);
  const activeAssessment = statusData?.activeAssessment;

  // Estado para controlar a visibilidade do componente do plano atual
  // O componente será exibido sempre após um novo login, mas pode ser fechado
  // Uma vez fechado, permanecerá fechado durante toda a sessão ativa
  const [isPlanCardVisible, setIsPlanCardVisible] = React.useState(() => {
    // Verifica se o componente foi fechado nesta sessão
    const isHidden = sessionStorage.getItem('planCardHidden') === 'true';
    return !isHidden;
  });

  // Função para fechar o componente e salvar o estado na sessão
  const handleClosePlanCard = () => {
    setIsPlanCardVisible(false);
    sessionStorage.setItem('planCardHidden', 'true');
  };

  // Flag para desabilitar temporariamente o componente de plano
  const isPlanCardEnabled = false;



  if (!user) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <div className="text-center py-12">
            <p className="text-slate-600">Usuário não encontrado</p>
            <Link to="/login">
              <Button className="mt-4">Fazer Login</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const subscriptionDetails = {
    free: {
      title: 'Plano Gratuito',
      description: 'Acesso básico ao teste de comunicação',
      color: 'gray',
    },
    premium: {
      title: 'Premium Individual',
      description: 'Relatório completo + plano de desenvolvimento',
      color: 'primary',
    },
    mentoring: {
      title: 'Premium Mentoria',
      description: 'Premium + 3 sessões de mentoria',
      color: 'secondary',
    },
    enterprise: {
      title: 'Empresarial',
      description: 'Solução completa para equipes',
      color: 'purple',
    },
  };

  const userTier = user.subscription_tier || user.SubscriptionTier || 'free';
  const currentPlan = subscriptionDetails[userTier as keyof typeof subscriptionDetails] || subscriptionDetails.free;

  return (
    <div className="space-y-8">
      <div className="max-w-4xl mx-auto space-y-8">
      {/* Welcome Header */}
      <Card className="bg-gradient-to-r from-primary-50 to-secondary-50 border-primary-100">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center space-x-3">
            <HybridAvatar
              name={user.name || 'Usuário'}
              src={user.profile_image || user.ProfileImageURL}
              size="lg"
            />
            <h1 className="text-2xl font-bold text-slate-900">
              Olá, {user.name}! 👋
            </h1>
          </div>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Bem-vinda ao seu painel de desenvolvimento comunicativo
          </p>
        </div>
      </Card>

      {/* Subscription Status - Só exibe se não foi fechado. desabilitado por enquanto */}
      {isPlanCardEnabled && isPlanCardVisible && (
        <Card className={`${
          userTier === 'free' 
            ? 'border-l-4 border-l-secondary-400 bg-gradient-to-r from-secondary-50/30 to-transparent' 
            : 'border-l-4 border-l-slate-200'
        } relative`}>
          {/* Botão de fechar */}
          <button
            onClick={handleClosePlanCard}
            className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 transition-colors duration-200 group"
            title="Fechar"
          >
            <svg 
              className="w-5 h-5 text-gray-400 group-hover:text-gray-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24" 
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  userTier === 'free' 
                    ? 'bg-gradient-to-br from-secondary-100 to-secondary-200' 
                    : 'bg-gradient-to-br from-blue-100 to-blue-200'
                }`}>
                  {userTier === 'free' ? (
                    <svg className="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  )}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-900">
                    {userTier === 'free' ? 'Plano Gratuito' : 'Plano Premium'}
                  </h3>
                  {userTier === 'free' && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800">
                      Limitado
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-slate-600 mt-1">
                  {userTier === 'free' 
                    ? 'Aproveite os recursos básicos. Considere fazer upgrade para desbloquear todo o potencial.'
                    : 'Você tem acesso completo a todos os recursos premium.'
                  }
                </p>

                {userTier === 'free' && (
                  <div className="mt-3 bg-white/50 rounded-lg p-3 border border-secondary-200/30">
                    <div className="flex items-center text-xs text-slate-600 mb-2">
                      <span>Recursos utilizados</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-600">Avaliações</span>
                        <span className="font-medium text-slate-700">2/3</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-1.5">
                        <div className="bg-secondary-400 h-1.5 rounded-full transition-all duration-300" style={{ width: '66%' }}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {userTier === 'free' && (
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button 
                  size="sm" 
                  className="flex-1 bg-gradient-to-r from-secondary-500 to-secondary-600 hover:from-secondary-600 hover:to-secondary-700 text-white font-medium"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Fazer Upgrade
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-secondary-300 text-secondary-700 hover:bg-secondary-50"
                >
                  Ver Recursos
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <div className="text-center space-y-4 p-2">
            <div className={`w-14 h-14 bg-gradient-to-br rounded-2xl flex items-center justify-center mx-auto shadow-lg ${
              hasCompleted 
                ? 'from-green-100 to-green-200 shadow-green-500/10' 
                : hasActive 
                  ? 'from-yellow-100 to-yellow-200 shadow-yellow-500/10'
                  : 'from-primary-100 to-primary-200 shadow-primary-500/10'
            }`}>
              {hasCompleted ? (
                <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : hasActive ? (
                <svg className="w-7 h-7 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-7 h-7 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              )}
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold text-slate-900 text-lg">
                {hasCompleted 
                  ? 'Avaliação Completa' 
                  : hasActive 
                    ? 'Continuar Avaliação'
                    : 'Nova Avaliação'
                }
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {hasCompleted 
                  ? 'Avaliação concluída! No momento permitimos apenas uma avaliação por usuário.' 
                  : hasActive 
                    ? `Continue de onde parou (${activeAssessment?.Progress?.CompletionPercent || 0}% completo)`
                    : 'Faça sua avaliação para descobrir seu perfil de comunicação'
                }
              </p>
            </div>
            <div className="pt-2">
              {hasCompleted ? (
                <Link to="/results">
                  <Button size="sm" className="w-full font-medium" variant="outline">
                    Ver Resultados
                  </Button>
                </Link>
              ) : (
                <Link to="/assessment">
                  <Button size="sm" className="w-full font-medium" disabled={isLoadingStatus}>
                    {hasActive ? 'Continuar' : 'Iniciar Teste'}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </Card>

        <AssessmentHistoryCard />

        <Card>
          <div className="text-center space-y-4 p-2">
            <div className="w-14 h-14 bg-gradient-to-br from-tertiary-100 to-tertiary-200 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-tertiary-500/10">
              <svg className="w-7 h-7 text-tertiary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold text-slate-900 text-lg">Recursos</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Acesse materiais exclusivos para desenvolvimento
              </p>
            </div>
            <div className="pt-2">
              <Button variant="outline" size="sm" className="w-full font-medium" disabled>
                Em breve
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Activity - DISABLED */}
      {false && (
        <Card>
          <div className="flex items-center space-x-2 mb-6">
            <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
            <h2 className="text-sm font-medium text-slate-600 uppercase tracking-wide">
              Atividade Recente
            </h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-4 pb-4 border-b border-slate-100 last:border-b-0 last:pb-0">
              <div className="flex-shrink-0 w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center mt-0.5">
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Conta criada</p>
                    <p className="text-xs text-slate-600 mt-1">
                      Bem-vinda à plataforma Comunicação! Complete seu primeiro teste para começar sua jornada.
                    </p>
                  </div>
                  <time className="text-xs text-slate-400 whitespace-nowrap ml-4">
                    {new Date(user?.created_at || user?.CreatedAt || new Date()).toLocaleDateString('pt-BR', { 
                      day: '2-digit', 
                      month: 'short' 
                    })}
                  </time>
                </div>
              </div>
            </div>
            
            {/* Placeholder for future activities */}
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm text-slate-500">Mais atividades aparecerão aqui conforme você usar a plataforma</p>
            </div>
          </div>
        </Card>
      )}

      {/* Help Section */}
      <Card>
        <div className="text-center space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">
            Precisa de ajuda?
          </h2>
          <p className="text-slate-600">
            Nossa equipe está aqui para apoiar seu desenvolvimento comunicativo
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline" 
              onClick={() => window.open('https://wa.me/5531972061387?text=Olá! Preciso de ajuda com a plataforma de comunicação.', '_blank')}
              className="flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.593z"/>
              </svg>
              Falar com Suporte
            </Button>
            {userTier === 'mentoring' && (
              <Button>
                Agendar Mentoria
              </Button>
            )}
          </div>
        </div>
      </Card>
      </div>
    </div>
  );
};